import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, RefreshCw, Smartphone } from 'lucide-react';
import {
  buildCommunicationMessageFormData,
  fetchConversationWhatsAppTemplates,
  fetchDriverCustomerChannelStatus,
  fetchConversationMessages,
  hasCommunicationMedia,
  markConversationRead,
  recordConversationDeliveryContextEvent,
  sendConversationMessage,
  sendConversationWhatsAppTemplate,
  sendDriverCustomerMessage,
} from './api';
import { socket, socketAuthPayload } from '../../admin/chat/socket';
import ConversationDeliveryContext from './ConversationDeliveryContext';
import DeliverySlotInlineSelector from './DeliverySlotInlineSelector';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';

export default function ConversationThread({
  conversationId,
  currentUserId,
  currentUserType = 'admin',
  className = '',
  showInput = true,
  allowNotes = true,
  inputPlaceholder = 'Escribe un mensaje interno...',
  inputHelperText,
  onConversationLoaded,
  autoScrollMessages = true,
  autoScrollOnInitialLoad = true,
  enableDriverCustomerChannels = false,
  enableFinalCustomerWhatsApp = false,
  enableDeliverySlotSelection = false,
  showDeliveryContext = false,
}) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [driverCustomerChannel, setDriverCustomerChannel] = useState(
    currentUserType === 'driver' ? 'fastmax_app' : 'internal'
  );
  const [driverCustomerStatus, setDriverCustomerStatus] = useState(null);
  const [customerWhatsAppTemplates, setCustomerWhatsAppTemplates] = useState([]);
  const [showCustomerWhatsAppTemplates, setShowCustomerWhatsAppTemplates] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSendingId, setTemplateSendingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesContainerRef = useRef(null);
  const suppressNextAutoScrollRef = useRef(false);
  const driverCustomerEnabled = (
    enableDriverCustomerChannels
    && currentUserType === 'driver'
    && conversation?.type === 'delivery'
    && conversation?.delivery_point_id
  );
  const finalCustomerWhatsAppEnabled = (
    enableFinalCustomerWhatsApp
    && ['admin', 'store', 'provider'].includes(currentUserType)
    && conversation?.type === 'delivery'
    && conversation?.delivery_point_id
  );
  const deliverySlotSelectionEnabled = (
    enableDeliverySlotSelection
    && ['store', 'provider'].includes(currentUserType)
    && conversation?.type === 'delivery'
    && conversation?.delivery_point_id
  );
  const deliveryCustomerStatusEnabled = driverCustomerEnabled || finalCustomerWhatsAppEnabled;
  const adminCustomerWhatsAppEnabled = finalCustomerWhatsAppEnabled && currentUserType === 'admin';
  const driverWhatsAppAvailable = Boolean(driverCustomerStatus?.whatsapp_available);
  const rawWhatsAppDisabledReason = driverCustomerStatus?.whatsapp_disabled_reason || (
    currentUserType === 'driver'
      ? 'WhatsApp no disponible. Usa Cliente App.'
      : 'WhatsApp no disponible. Usa Interno Fast Max.'
  );
  const driverWhatsAppDisabledReason = currentUserType === 'driver'
    ? rawWhatsAppDisabledReason
    : rawWhatsAppDisabledReason.replace('Usa Cliente App.', 'Usa Interno Fast Max.');

  const canUseDeliveryCustomerChannels = useCallback((nextConversation) => (
    nextConversation?.type === 'delivery'
    && nextConversation?.delivery_point_id
    && (
      (enableDriverCustomerChannels && currentUserType === 'driver')
      || (enableFinalCustomerWhatsApp && ['admin', 'store', 'provider'].includes(currentUserType))
    )
  ), [currentUserType, enableDriverCustomerChannels, enableFinalCustomerWhatsApp]);

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchConversationMessages(conversationId, {
        currentUserType,
        currentUserId,
      });
      setConversation(data.conversation);
      onConversationLoaded?.(data.conversation);
      if (canUseDeliveryCustomerChannels(data.conversation)) {
        try {
          const status = await fetchDriverCustomerChannelStatus(conversationId);
          setDriverCustomerStatus(status || null);
          if (!status?.whatsapp_available) {
            setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
          }
        } catch (statusError) {
          console.error('Customer WhatsApp status error:', statusError);
          setDriverCustomerStatus({
            whatsapp_available: false,
            whatsapp_disabled_reason: 'No se pudo verificar WhatsApp. Actualiza para reintentar.',
          });
          setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
        }
      } else {
        setDriverCustomerStatus(null);
        setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
      }
      if (!autoScrollOnInitialLoad) {
        suppressNextAutoScrollRef.current = true;
      }
      setMessages(data.messages || []);
      await markConversationRead(conversationId, {
        recipient_type: currentUserType,
        user_id: currentUserId,
        driver_id: currentUserType === 'driver' ? currentUserId : undefined,
      });
    } catch (err) {
      console.error('Communication thread error:', err);
      setError('No se pudo cargar la conversación');
    } finally {
      setIsLoading(false);
    }
  }, [
    autoScrollOnInitialLoad,
    canUseDeliveryCustomerChannels,
    conversationId,
    currentUserId,
    currentUserType,
    onConversationLoaded,
  ]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
    setDriverCustomerStatus(null);
    setCustomerWhatsAppTemplates([]);
    setShowCustomerWhatsAppTemplates(false);
    setConversation(null);
    setMessages([]);
    setError(null);
  }, [conversationId, currentUserType]);

  useEffect(() => {
    if (!conversationId) return undefined;

    socket.emit('join_conversation', socketAuthPayload({ conversation_id: conversationId }));
    const handleMessage = (message) => {
      const payloadMessage = message?.message || message;
      if (Number(payloadMessage?.conversation_id) !== Number(conversationId)) return;
      if (!payloadMessage?.id) return;
      setMessages(prev => {
        if (prev.some(item => item.id === payloadMessage.id && item.channel === payloadMessage.channel)) return prev;
        return [...prev, payloadMessage];
      });
    };
    const handleConversationUpdated = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (payload.conversation) {
        setConversation(payload.conversation);
        onConversationLoaded?.(payload.conversation);
      }
    };
    const handleReadUpdated = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (payload.conversation) {
        setConversation(payload.conversation);
        onConversationLoaded?.(payload.conversation);
      }
    };
    const handleConnect = () => {
      socket.emit('join_conversation', socketAuthPayload({ conversation_id: conversationId }));
      loadMessages();
    };

    socket.on('conversation_message_created', handleMessage);
    socket.on('conversation_updated', handleConversationUpdated);
    socket.on('conversation_read_updated', handleReadUpdated);
    socket.on('connect', handleConnect);

    return () => {
      socket.off('conversation_message_created', handleMessage);
      socket.off('conversation_updated', handleConversationUpdated);
      socket.off('conversation_read_updated', handleReadUpdated);
      socket.off('connect', handleConnect);
      socket.emit('leave_conversation', socketAuthPayload({ conversation_id: conversationId }));
    };
  }, [conversationId, loadMessages, onConversationLoaded]);

  useEffect(() => {
    if (!autoScrollMessages) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false;
      return;
    }

    const scrollToBottom = () => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    };

    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(scrollToBottom);
    } else {
      scrollToBottom();
    }
  }, [autoScrollMessages, messages]);

  const handleDeliverySlotUpdated = useCallback((deliveryPoint) => {
    if (!deliveryPoint?.id) return;
    setConversation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        delivery: {
          ...(prev.delivery || {}),
          ...deliveryPoint,
        },
      };
    });
  }, []);

  const handleDeliveryContextEvent = useCallback((eventType) => {
    if (!conversationId) return;
    recordConversationDeliveryContextEvent(
      conversationId,
      eventType,
      currentUserType === 'store' ? 'store_chat' : 'conversation_thread',
    ).catch(eventError => {
      // Observability is best effort and must never interrupt the chat.
      console.error('No se pudo registrar la interaccion con la entrega:', eventError);
    });
  }, [conversationId, currentUserType]);

  async function handleToggleCustomerWhatsAppTemplates() {
    if (showCustomerWhatsAppTemplates) {
      setShowCustomerWhatsAppTemplates(false);
      return;
    }
    setTemplatesLoading(true);
    setError(null);
    try {
      const data = await fetchConversationWhatsAppTemplates(conversationId);
      setCustomerWhatsAppTemplates(data.templates || []);
      setShowCustomerWhatsAppTemplates(true);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'No se pudieron cargar las plantillas WhatsApp.');
    } finally {
      setTemplatesLoading(false);
    }
  }

  async function handleSendCustomerWhatsAppTemplate(templateId) {
    setTemplateSendingId(templateId);
    setError(null);
    try {
      const result = await sendConversationWhatsAppTemplate(conversationId, templateId);
      if (result.message) {
        setMessages(prev => prev.some(item => item.id === result.message.id && item.channel === result.message.channel)
          ? prev : [...prev, result.message]);
      }
      try {
        const status = await fetchDriverCustomerChannelStatus(conversationId);
        setDriverCustomerStatus(status || null);
        if (!status?.whatsapp_available) setDriverCustomerChannel('internal');
      } catch (statusError) {
        console.error('Customer WhatsApp status refresh error:', statusError);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'No se pudo enviar la plantilla WhatsApp.');
    } finally {
      setTemplateSendingId(null);
    }
  }

  async function handleSend(payload) {
    let message;
    try {
      if (driverCustomerEnabled) {
        const selectedChannel = driverCustomerChannel === 'whatsapp' && driverWhatsAppAvailable
          ? 'whatsapp'
          : 'fastmax_app';
        const requestPayload = hasCommunicationMedia(payload)
          ? buildCommunicationMessageFormData({
              ...payload,
              channel: selectedChannel,
            })
          : {
              content: payload.content,
              channel: selectedChannel,
            };
        const result = await sendDriverCustomerMessage(conversationId, requestPayload);
        message = result.message;
        if (selectedChannel === 'whatsapp') {
          const status = await fetchDriverCustomerChannelStatus(conversationId);
          setDriverCustomerStatus(status || null);
        }
      } else if (finalCustomerWhatsAppEnabled && driverCustomerChannel === 'whatsapp') {
        if (!driverWhatsAppAvailable) {
          throw new Error(driverWhatsAppDisabledReason);
        }
        const requestPayload = hasCommunicationMedia(payload)
          ? buildCommunicationMessageFormData({
              ...payload,
              channel: 'whatsapp',
            })
          : {
              content: payload.content,
              channel: 'whatsapp',
            };
        const result = await sendDriverCustomerMessage(conversationId, requestPayload);
        message = result.message;
        try {
          const status = await fetchDriverCustomerChannelStatus(conversationId);
          setDriverCustomerStatus(status || null);
          if (!status?.whatsapp_available) setDriverCustomerChannel('internal');
        } catch (statusError) {
          console.error('Customer WhatsApp status refresh error:', statusError);
        }
      } else {
        const requestPayload = {
          ...payload,
          sender_type: currentUserType,
          sender_user_id: currentUserId,
          sender_driver_id: currentUserType === 'driver' ? currentUserId : undefined,
        };
        message = await sendConversationMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(requestPayload) : requestPayload
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      const messageText = err?.response?.data?.message || err?.response?.data?.error || err.message || 'No se pudo enviar el mensaje.';
      if (status === 428 && deliveryCustomerStatusEnabled) {
        setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
        setDriverCustomerStatus(prev => ({
          ...(prev || {}),
          whatsapp_available: false,
          whatsapp_disabled_reason: messageText,
        }));
      }
      setError(messageText);
      throw err;
    }
    if (!message) return;
    setMessages(prev => {
      if (prev.some(item => item.id === message.id && item.channel === message.channel)) return prev;
      return [...prev, message];
    });
  }

  if (!conversationId) {
    return (
      <div className={`flex items-center justify-center bg-slate-50 text-slate-500 ${className}`}>
        Selecciona una conversación
      </div>
    );
  }

  return (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 sm:min-h-[420px] ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-3 sm:px-4">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-extrabold text-slate-900 font-montserrat">
            {showDeliveryContext && conversation?.delivery
              ? (conversation.delivery.recipient_name || 'Cliente final')
              : 'Comunicacion Fast Max'}
          </h3>
          <p className="truncate text-xs text-slate-500 font-montserrat">
            {conversation?.delivery_point_id
              ? `Entrega #${conversation.delivery_point_id}${conversation?.delivery?.status ? ` | ${conversation.delivery.status}` : ''}`
              : `Conversacion #${conversationId}`}
          </p>
        </div>
        <button
          onClick={loadMessages}
          disabled={isLoading}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50 sm:py-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {showDeliveryContext && conversation?.delivery_point_id && (
        <ConversationDeliveryContext
          delivery={{
            ...(conversation.delivery || {}),
            id: conversation.delivery?.id || conversation.delivery_point_id,
          }}
          onEvent={handleDeliveryContextEvent}
        />
      )}

      {deliverySlotSelectionEnabled && (
        <DeliverySlotInlineSelector
          conversationId={conversationId}
          conversation={conversation}
          onSlotUpdated={handleDeliverySlotUpdated}
        />
      )}

      <div ref={messagesContainerRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 sm:p-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!error && messages.length === 0 && !isLoading && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            Sin mensajes todavía. Usa este hilo para centralizar notas, WhatsApp y coordinación interna.
          </div>
        )}

        {messages.map(message => (
          <MessageBubble key={`${message.channel}-${message.id}`} message={message} currentUserType={currentUserType} />
        ))}
      </div>

      {showInput && (
        <>
          {driverCustomerEnabled && (
            <div className="border-t border-slate-200 bg-white px-2 py-2 sm:px-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDriverCustomerChannel('fastmax_app')}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold sm:flex-none ${
                    driverCustomerChannel === 'fastmax_app' ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Cliente App
                </button>
                <button
                  type="button"
                  onClick={() => driverWhatsAppAvailable && setDriverCustomerChannel('whatsapp')}
                  disabled={!driverWhatsAppAvailable}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none ${
                    driverCustomerChannel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'
                  }`}
                  title={driverWhatsAppAvailable ? 'Enviar por WhatsApp' : driverWhatsAppDisabledReason}
                >
                  <Smartphone className="h-4 w-4" />
                  WhatsApp
                </button>
                {!driverWhatsAppAvailable && (
                  <span className="text-[11px] font-semibold text-amber-700">{driverWhatsAppDisabledReason}</span>
                )}
              </div>
            </div>
          )}
          {finalCustomerWhatsAppEnabled && (
            <div className="border-t border-slate-200 bg-white px-2 py-2 sm:px-3">
              {adminCustomerWhatsAppEnabled && (
                <p className="mb-2 text-[11px] font-semibold text-slate-600">
                  WhatsApp cliente final: {driverCustomerStatus?.phone_number || 'número no disponible'}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDriverCustomerChannel('internal')}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold sm:flex-none ${
                    driverCustomerChannel === 'internal' ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Interno Fast Max
                </button>
                <button
                  type="button"
                  onClick={() => driverWhatsAppAvailable && setDriverCustomerChannel('whatsapp')}
                  disabled={!driverWhatsAppAvailable}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none ${
                    driverCustomerChannel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'
                  }`}
                  title={driverWhatsAppAvailable ? 'Enviar al WhatsApp del cliente final' : driverWhatsAppDisabledReason}
                >
                  <Smartphone className="h-4 w-4" />
                  WhatsApp cliente
                </button>
                {!driverWhatsAppAvailable && (
                  <span className="text-[11px] font-semibold text-amber-700">{driverWhatsAppDisabledReason}</span>
                )}
                {adminCustomerWhatsAppEnabled && driverCustomerStatus?.phone_number && (
                  <button
                    type="button"
                    onClick={handleToggleCustomerWhatsAppTemplates}
                    disabled={templatesLoading || Boolean(templateSendingId)}
                    className="inline-flex min-h-[40px] items-center justify-center rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 disabled:opacity-50"
                  >
                    {templatesLoading ? 'Cargando...' : 'Plantillas WhatsApp'}
                  </button>
                )}
              </div>
              {adminCustomerWhatsAppEnabled && showCustomerWhatsAppTemplates && (
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {customerWhatsAppTemplates.map(template => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleSendCustomerWhatsAppTemplate(template.id)}
                      disabled={!template.available || Boolean(templateSendingId)}
                      title={template.disabled_reason || template.description}
                      className="rounded-lg border border-slate-200 bg-white p-2 text-left text-xs hover:border-[#B1336E] disabled:opacity-50"
                    >
                      <span className="block font-bold text-slate-900">{template.label}</span>
                      <span className="text-slate-500">{templateSendingId === template.id ? 'Enviando...' : template.disabled_reason || template.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <MessageInput
            onSend={handleSend}
            disabled={isLoading || Boolean(templateSendingId)}
            allowNotes={!driverCustomerEnabled && driverCustomerChannel !== 'whatsapp' && allowNotes}
            placeholder={driverCustomerEnabled
              ? (driverCustomerChannel === 'whatsapp' ? 'Escribe al WhatsApp del cliente...' : 'Escribe al cliente final...')
              : finalCustomerWhatsAppEnabled && driverCustomerChannel === 'whatsapp'
                ? 'Escribe al WhatsApp del cliente final...'
                : inputPlaceholder}
            helperText={driverCustomerEnabled
              ? (driverCustomerChannel === 'whatsapp'
                  ? 'Mensaje libre al WhatsApp del cliente final. Solo disponible con ventana de 24h abierta.'
                  : 'Mensaje visible para el cliente final dentro de Fast Max App y registrado en esta entrega.')
              : finalCustomerWhatsAppEnabled && driverCustomerChannel === 'whatsapp'
                ? 'Mensaje libre al WhatsApp del cliente final. Solo disponible con ventana de 24h abierta y queda registrado en esta entrega.'
                : inputHelperText}
          />
        </>
      )}
    </div>
  );
}
